import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useCallback, useContext, useEffect, useRef, } from 'react';
import { createPortal } from 'react-dom';
import { ModalDispatchContext, ModalStateContext } from './ModalContext';
import { motion, AnimatePresence } from 'framer-motion';
const ModalHandler = () => {
    var _a, _b;
    const modalState = useContext(ModalStateContext);
    const modalDispatch = useContext(ModalDispatchContext);
    const ModalComponent = modalState === null || modalState === void 0 ? void 0 : modalState.ModalComponent;
    const ResolvedModalComponent = ModalComponent;
    const closeModal = modalDispatch === null || modalDispatch === void 0 ? void 0 : modalDispatch.closeModal;
    const dialogRef = useRef(null);
    const isClosingRef = useRef(false);
    const modal = modalState === null || modalState === void 0 ? void 0 : modalState.modal;
    const previousActiveElementRef = useRef(null);
    const handleClose = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!closeModal || isClosingRef.current) {
            return;
        }
        isClosingRef.current = true;
        try {
            yield closeModal();
        }
        finally {
            isClosingRef.current = false;
        }
    }), [closeModal]);
    useEffect(() => {
        if (!modal)
            return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [modal]);
    const canRenderModal = Boolean(modal && ResolvedModalComponent && closeModal);
    const activeModalName = modal === null || modal === void 0 ? void 0 : modal.name;
    const rawModalProps = ((_a = modal === null || modal === void 0 ? void 0 : modal.props) !== null && _a !== void 0 ? _a : {});
    const ariaLabelledBy = typeof rawModalProps.ariaLabelledBy === 'string'
        ? rawModalProps.ariaLabelledBy
        : undefined;
    const ariaLabel = typeof rawModalProps.ariaLabel === 'string'
        ? rawModalProps.ariaLabel
        : 'Modal dialog';
    const disableEscapeClose = rawModalProps.disableEscapeClose === true;
    const disableBackdropClose = rawModalProps.disableBackdropClose === true;
    const backdropBlur = rawModalProps.backdropBlur;
    const isFullWindow = rawModalProps.fullWindow === true || rawModalProps.variant === 'fullWindow';
    const onEscapeAttempt = typeof rawModalProps.onEscapeAttempt === 'function'
        ? rawModalProps.onEscapeAttempt
        : undefined;
    const onBackdropAttempt = typeof rawModalProps.onBackdropAttempt === 'function'
        ? rawModalProps.onBackdropAttempt
        : undefined;
    const modalProps = Object.assign(Object.assign({}, rawModalProps), { ariaLabel: undefined, ariaLabelledBy: undefined, backdropBlur: undefined, disableBackdropClose: undefined, disableEscapeClose: undefined, onBackdropAttempt: undefined, onEscapeAttempt: undefined, fullWindow: isFullWindow, variant: rawModalProps.variant });
    const backdropStyle = Object.assign(Object.assign(Object.assign({}, styles.backdrop), (isFullWindow ? styles.fullWindowBackdrop : {})), { backdropFilter: typeof backdropBlur === 'number'
            ? `blur(${backdropBlur}px)`
            : typeof backdropBlur === 'string' && backdropBlur
                ? `blur(${backdropBlur})`
                : undefined });
    const dialogStyle = Object.assign(Object.assign({}, styles.modal), (isFullWindow ? styles.fullWindowModal : {}));
    const runCloseAttempt = useCallback((isCloseDisabled, attemptHandler) => __awaiter(void 0, void 0, void 0, function* () {
        if (attemptHandler) {
            try {
                const shouldContinue = yield attemptHandler();
                if (shouldContinue === false) {
                    return;
                }
            }
            catch (error) {
                console.error('Modal close attempt handler failed:', error);
                return;
            }
        }
        if (isCloseDisabled) {
            return;
        }
        yield handleClose();
    }), [handleClose]);
    const handleBackdropClick = useCallback(() => {
        return runCloseAttempt(disableBackdropClose, onBackdropAttempt);
    }, [disableBackdropClose, onBackdropAttempt, runCloseAttempt]);
    const handleEscapeKeyAttempt = useCallback(() => {
        return runCloseAttempt(disableEscapeClose, onEscapeAttempt);
    }, [disableEscapeClose, onEscapeAttempt, runCloseAttempt]);
    const getFocusableElements = useCallback(() => {
        if (!dialogRef.current) {
            return [];
        }
        const focusableSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.from(dialogRef.current.querySelectorAll(focusableSelector)).filter((element) => element.getAttribute('aria-hidden') !== 'true');
    }, []);
    const focusInitialElement = useCallback(() => {
        const dialogElement = dialogRef.current;
        if (!dialogElement) {
            return false;
        }
        const [firstFocusable] = getFocusableElements();
        if (firstFocusable) {
            firstFocusable.focus();
            return true;
        }
        dialogElement.focus();
        return false;
    }, [getFocusableElements]);
    useEffect(() => {
        if (!canRenderModal) {
            return;
        }
        previousActiveElementRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        const didFocusInteractiveElement = focusInitialElement();
        let observer = null;
        if (!didFocusInteractiveElement && dialogRef.current) {
            observer = new MutationObserver(() => {
                const dialogElement = dialogRef.current;
                if (!dialogElement) {
                    return;
                }
                const [firstFocusable] = getFocusableElements();
                if (!firstFocusable) {
                    return;
                }
                if (document.activeElement === dialogElement) {
                    firstFocusable.focus();
                    observer === null || observer === void 0 ? void 0 : observer.disconnect();
                }
            });
            observer.observe(dialogRef.current, { childList: true, subtree: true });
        }
        return () => {
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            const previousActiveElement = previousActiveElementRef.current;
            if (previousActiveElement === null || previousActiveElement === void 0 ? void 0 : previousActiveElement.isConnected) {
                previousActiveElement.focus();
                return;
            }
            if (document.body.isConnected) {
                document.body.focus();
            }
        };
    }, [
        canRenderModal,
        activeModalName,
        focusInitialElement,
        getFocusableElements,
    ]);
    useEffect(() => {
        if (!canRenderModal) {
            return;
        }
        const onKeyDown = (event) => {
            var _a;
            if (event.key === 'Escape') {
                if (event.defaultPrevented) {
                    return;
                }
                const dialogElement = dialogRef.current;
                const target = event.target;
                const isEventInsideDialog = Boolean(dialogElement) && target instanceof Node
                    ? dialogElement === null || dialogElement === void 0 ? void 0 : dialogElement.contains(target)
                    : false;
                if (!isEventInsideDialog) {
                    return;
                }
                event.preventDefault();
                void handleEscapeKeyAttempt();
                return;
            }
            if (event.key !== 'Tab') {
                return;
            }
            const focusable = getFocusableElements();
            if (focusable.length === 0) {
                event.preventDefault();
                (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [canRenderModal, getFocusableElements, handleEscapeKeyAttempt]);
    // Animation variants for the backdrop (fades in)
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };
    // Animate both axes explicitly to avoid perceived axis-staggered growth.
    const modalVariants = {
        hidden: { scaleX: 0.82, scaleY: 0.82, opacity: 0 },
        visible: { scaleX: 1, scaleY: 1, opacity: 1 },
        exit: { scaleX: 0.82, scaleY: 0.82, opacity: 0 },
    };
    const modalContent = (_jsx(AnimatePresence, { children: canRenderModal && modal && ResolvedModalComponent ? (_jsx(motion.div, { className: "backdrop", variants: backdropVariants, initial: "hidden", animate: "visible", exit: "hidden", onClick: () => {
                void handleBackdropClick();
            }, style: backdropStyle, children: _jsx(Suspense, { fallback: _jsx(_Fragment, {}), children: _jsx(motion.div, { ref: dialogRef, className: "modal", role: "dialog", "aria-modal": "true", "aria-labelledby": ariaLabelledBy, "aria-label": ariaLabelledBy ? undefined : ariaLabel, tabIndex: -1, variants: modalVariants, transition: {
                        type: 'tween',
                        duration: 0.18,
                        ease: [0.22, 1, 0.36, 1],
                    }, 
                    // Prevent clicks inside the modal from closing it
                    onClick: (e) => e.stopPropagation(), style: dialogStyle, children: _jsx(ResolvedModalComponent, Object.assign({ onClose: handleClose }, modalProps)) }) }) }, modal === null || modal === void 0 ? void 0 : modal.name)) : null }));
    if (typeof document === 'undefined') {
        return null;
    }
    const portalTarget = (_b = document.getElementById('modal-root')) !== null && _b !== void 0 ? _b : document.body;
    return createPortal(modalContent, portalTarget);
};
// Basic styles
const styles = {
    backdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2147483646,
    },
    modal: {
        backgroundColor: 'transparent',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        transformOrigin: 'center center',
    },
    fullWindowBackdrop: {
        justifyContent: 'stretch',
        alignItems: 'stretch',
        padding: 0,
    },
    fullWindowModal: {
        width: '100vw',
        height: '100vh',
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'auto',
    },
};
export default ModalHandler;
